/**
 * Regime stack — price, its smoothed body, and the higher-timeframe context
 * band read as one ladder.
 *
 * THE RULE. Three levels have to agree, in order, for the background to commit:
 *
 *     bullish   close  >  mid  >  band.upper
 *     bearish   close  <  mid  <  band.lower
 *     neutral   anything else
 *
 * `close` is always the REAL close of the chart-timeframe bar — not the
 * displayed candle's close. On a Heikin-Ashi chart the drawn close is already a
 * smoothed number, and comparing a smoothed close against a smoothed body would
 * be comparing a thing to itself. `mid` is the smoothed body's close for the
 * chosen candle type, and the band is the covering HIGHER-timeframe body.
 *
 * WHY "CLEAR OF THE WHOLE BAND", not "above its midpoint". The band is a body,
 * not a line. Requiring the middle level to be outside it entirely is what
 * gives `neutral` something to mean: while price is working inside the
 * higher-timeframe body there is no regime to report, and saying so is more
 * useful than rounding it to bull or bear.
 *
 * 'Regular' DROPS THE MIDDLE RUNG. With no smoothing, `mid` would be the same
 * number as `close`, so `close > mid` could never be true and the rule would
 * report neutral on every bar for ever. It degrades to `close vs band` instead
 * — the same idea with one comparison fewer, rather than a setting that
 * silently does nothing.
 *
 * ── CAUSALITY: READ THIS BEFORE BUILDING A RULE ON IT ───────────────────────
 *
 * The band is the higher-timeframe body CONTAINING each bar, which is the body
 * drawn on screen — and that body CLOSES IN THE BAR'S FUTURE. That is correct
 * for a background tint, whose whole job is to agree with the band the eye is
 * comparing against, and it is INVALID as a gate. A Vajra gate built on exactly
 * this stamping measured +1.48% of pure hindsight and collapsed to +0.12% once
 * made causal. Anything that acts rather than draws must re-stamp from the last
 * higher-timeframe bar to have actually CLOSED; dropping the forming bar is not
 * enough, because historical bars still get their own containing body.
 *
 * `forming` is reported rather than hidden, so a caller can see which stamps
 * come from a body that is still moving.
 *
 * This module is pure: no React, no rendering, no lightweight-charts types.
 * Byte-identical twin at backend/src/services/strategies/chartPort/regimeStack.ts,
 * compared by execution in backend/src/scripts/bigmove/parity.ts.
 */
import { CandleRow } from '../components/charts/TradingChart';
import { transformCandles } from './candleTransforms';
import { computeClnBars, CLN_DEFAULT_LENGTH } from './clnCandles';

export type StackCandleType = 'Regular' | 'HA' | 'CLH' | 'CLN';
export type StackRegime = 'bull' | 'bear' | 'neutral';

export interface StackBody {
  time: number;
  upper: number;
  lower: number;
}

export interface StackStamp {
  time: number;
  /** The bar's REAL close. */
  close: number;
  /** The smoothed body's close. null for 'Regular' — see the note above. */
  mid: number | null;
  /** The covering higher-timeframe body. */
  upper: number;
  lower: number;
  /** The covering higher-timeframe bar had not closed yet. */
  forming: boolean;
  regime: StackRegime;
}

/**
 * Body extremes per bar for a candle type.
 *
 * CLN is not a geometry — `transformCandles(x, 'CLN')` is a passthrough, because
 * CLN draws a regression body OVER the real candle rather than replacing it. So
 * it is the one type that has to come from computeClnBars. Getting this wrong
 * would silently hand back the RAW body under a CLN label, and every comparison
 * against it would still look plausible.
 */
export function stackBodies(
  bars: CandleRow[],
  type: StackCandleType,
  clnLength: number = CLN_DEFAULT_LENGTH,
): StackBody[] {
  if (!bars?.length) return [];
  if (type === 'CLN') {
    return computeClnBars(bars, clnLength).map(b => ({
      time: b.time, upper: b.lrBodyHigh, lower: b.lrBodyLow,
    }));
  }
  const src = type === 'Regular' ? bars : transformCandles(bars, type);
  return src.map(c => ({
    time: c.time as number,
    upper: Math.max(Number(c.open), Number(c.close)),
    lower: Math.min(Number(c.open), Number(c.close)),
  }));
}

/** The middle rung per bar, or all-null for 'Regular'. */
export function stackMids(
  bars: CandleRow[],
  type: StackCandleType,
  clnLength: number = CLN_DEFAULT_LENGTH,
): Array<number | null> {
  if (!bars?.length) return [];
  if (type === 'Regular') return bars.map(() => null);
  if (type === 'CLN') return computeClnBars(bars, clnLength).map(b => b.lrClose);
  return transformCandles(bars, type).map(c => Number(c.close));
}

/** The rule itself, isolated so it can be read and tested on its own. */
export function classifyStack(
  close: number,
  mid: number | null,
  upper: number,
  lower: number,
): StackRegime {
  if (!Number.isFinite(close) || !Number.isFinite(upper) || !Number.isFinite(lower)) return 'neutral';
  if (mid == null) {
    // 'Regular' — the middle rung collapses onto the first, so it is dropped.
    if (close > upper) return 'bull';
    if (close < lower) return 'bear';
    return 'neutral';
  }
  if (!Number.isFinite(mid)) return 'neutral';
  if (close > mid && mid > upper) return 'bull';
  if (close < mid && mid < lower) return 'bear';
  return 'neutral';
}

/**
 * Stamp every chart-timeframe bar with its regime.
 *
 * @param ltfBars    the chart's own bars, ascending
 * @param htfBars    the mapped higher-timeframe bars, ascending. The caller
 *                   decides whether the still-open bar is included.
 * @param htfMinutes higher-timeframe bar size, used to know when a window ends
 * @param closedLen  how many leading `htfBars` have CLOSED; stamps sourced at or
 *                   beyond this index are marked `forming`
 * @param type       which candle type supplies the middle rung and the band
 * @param clnLength  regression length (settings.candle.clnLength)
 *
 * JOINED ON BAR TIME, NEVER ON INDEX. The two series have unrelated lengths and
 * unrelated grids — the same reason clnHtfBand says so in capitals. A length
 * check here would not be a safety net, it would be a feature that silently
 * disables itself, which is exactly how the BBR/Wave source setting once
 * shipped completely inert while every screenshot looked fine.
 */
export function computeRegimeStack(
  ltfBars: CandleRow[],
  htfBars: CandleRow[],
  htfMinutes: number,
  closedLen: number,
  type: StackCandleType,
  clnLength: number = CLN_DEFAULT_LENGTH,
): StackStamp[] {
  if (!ltfBars?.length || !htfBars?.length || !(htfMinutes > 0)) return [];
  const bodies = stackBodies(htfBars, type, clnLength);
  if (bodies.length < 2) return [];
  const mids = stackMids(ltfBars, type, clnLength);

  const durSec = htfMinutes * 60;
  const out: StackStamp[] = [];
  // One walk over both series rather than a lookup per bar.
  let k = 0;
  for (let i = 0; i < ltfBars.length; i++) {
    const t = ltfBars[i].time as number;
    while (k + 1 < bodies.length && bodies[k + 1].time <= t) k++;
    const b = bodies[k];
    if (!b || b.time > t) continue;             // before the first HTF bar opens
    const forming = k >= closedLen;
    // Past this HTF bar's own window there is no context — EXCEPT for the
    // still-forming bar, whose window is open by definition. Same condition as
    // the drawn band, so the tint cannot outlive the band it is reading.
    if (!forming && t >= b.time + durSec) continue;
    const close = Number(ltfBars[i].close);
    const mid = mids[i] == null ? null : Number(mids[i]);
    out.push({
      time: t,
      close,
      mid,
      upper: b.upper,
      lower: b.lower,
      forming,
      regime: classifyStack(close, mid, b.upper, b.lower),
    });
  }
  return out;
}
